import React, { useState, useEffect } from 'react';

function MobileDashboard({ view, setView, setActiveTab }) {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 🚨 FIXED: Action Handlers with Robust Event Firing 🚨
  const handleNavigation = (tabName, targetView = 'account') => {
    if (setActiveTab) {
      setActiveTab(tabName);
    }
    setView(targetView);
    
    // Account পেজটি লোড হতে কয়েক মিলিসেকেন্ড সময় নেয়, 
    // তাই মেনু হাইড করার সিগন্যালটি কয়েক ধাপে পাঠানো হলো যাতে কোনোভাবেই মিস না হয়।
    if (targetView === 'account') {
      const delays = [50, 150, 300, 500, 800];
      delays.forEach(delay => {
        setTimeout(() => {
          window.dispatchEvent(new Event('closeAccountMenu'));
        }, delay);
      });
    }
  };

  return (
    <div className="min-h-screen bg-[#f4f7f6] pb-24 font-sans flex flex-col">
      <style>
        {`
          @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
          .animate-slideUp { animation: slideUp 0.4s ease-out forwards; }
        `}
      </style>

      {/* Animated Deep Blue Header */}
      <div className={`fixed top-0 left-0 right-0 z-[60] transition-all duration-300 ${isScrolled ? 'bg-[#012b72] shadow-md py-3' : 'bg-[#012b72] pt-4 pb-6 shadow-none rounded-b-[1.5rem]'}`}>
        <div className="px-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => setView('home')} className="bg-white/20 w-8 h-8 rounded-full border-none text-white text-lg flex items-center justify-center font-bold cursor-pointer hover:bg-white/30 transition-colors">←</button>
            <h2 className="m-0 font-black text-[16px] text-white uppercase tracking-wide">
              My Dashboard
            </h2>
          </div>
          <div className={`transition-opacity duration-300 ${isScrolled ? 'opacity-0' : 'opacity-100'}`}>
             <span className="text-white/80 text-xl">🎛️</span>
          </div>
        </div>
      </div>

      <div className={`transition-all duration-300 ${isScrolled ? 'h-[60px]' : 'h-[100px]'}`}></div>

      {/* Welcome Banner */}
      <div className="px-4 mb-6 animate-slideUp">
        <div className="bg-gradient-to-br from-[#002f34] to-[#012b72] rounded-3xl p-6 shadow-lg relative overflow-hidden">
          <div className="relative z-10">
            <h3 className="text-white font-black text-xl m-0 mb-1 uppercase tracking-wide">Welcome Back!</h3>
            <p className="text-[#8ab4f8] font-bold text-xs m-0">Manage your cars, ads, and bookings all in one place.</p>
          </div>
          <div className="absolute -right-4 -bottom-6 text-[80px] opacity-10 transform -rotate-12">🚗</div>
        </div>
      </div>

      {/* 2x2 Grid with Premium SVG Icons */}
      <div className="px-4 grid grid-cols-2 gap-4 animate-slideUp" style={{ animationDelay: '0.1s' }}>
        
        {/* 1. Analytics */}
        <div onClick={() => handleNavigation('Analytics')} className="bg-white rounded-3xl p-5 flex flex-col items-center justify-center shadow-[0_8px_20px_rgba(0,0,0,0.04)] cursor-pointer active:scale-95 transition-all hover:shadow-lg border border-gray-50 aspect-square">
          <div className="w-20 h-20 mb-3 drop-shadow-[0_4px_10px_rgba(139,92,246,0.3)]">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
              <defs>
                <linearGradient id="gradAnalytics" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#C084FC" />
                  <stop offset="100%" stopColor="#7C3AED" />
                </linearGradient>
              </defs>
              <rect x="3" y="14" width="4" height="7" rx="1" fill="url(#gradAnalytics)" />
              <rect x="10" y="10" width="4" height="11" rx="1" fill="url(#gradAnalytics)" opacity="0.8" />
              <rect x="17" y="4" width="4" height="17" rx="1" fill="url(#gradAnalytics)" opacity="0.6" />
              <path d="M3 10L10 6L16 9L21 2" stroke="#F59E0B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <span className="text-[13px] font-black text-[#002f34] uppercase tracking-wide">Analytics</span>
        </div>

        {/* 2. Post Ad */}
        <div onClick={() => handleNavigation('Post Ad', 'sell_car_new')} className="bg-white rounded-3xl p-5 flex flex-col items-center justify-center shadow-[0_8px_20px_rgba(0,0,0,0.04)] cursor-pointer active:scale-95 transition-all hover:shadow-lg border border-gray-50 aspect-square">
          <div className="w-20 h-20 mb-3 drop-shadow-[0_4px_10px_rgba(249,115,22,0.3)]">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
              <defs>
                <linearGradient id="gradPostAd" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#FDBA74" />
                  <stop offset="100%" stopColor="#EA580C" />
                </linearGradient>
              </defs>
              <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" fill="url(#gradPostAd)" fillOpacity="0.15"/>
              <path d="M12 7V17" stroke="url(#gradPostAd)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M7 12H17" stroke="url(#gradPostAd)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="12" cy="12" r="10" stroke="url(#gradPostAd)" strokeWidth="2"/>
            </svg>
          </div>
          <span className="text-[13px] font-black text-[#002f34] uppercase tracking-wide">Post Ad</span>
        </div>

        {/* 3. My Bookings */}
        <div onClick={() => handleNavigation('My Bookings')} className="bg-white rounded-3xl p-5 flex flex-col items-center justify-center shadow-[0_8px_20px_rgba(0,0,0,0.04)] cursor-pointer active:scale-95 transition-all hover:shadow-lg border border-gray-50 aspect-square">
          <div className="w-20 h-20 mb-3 drop-shadow-[0_4px_10px_rgba(16,185,129,0.3)]">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
              <defs>
                <linearGradient id="gradBookings" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#6EE7B7" />
                  <stop offset="100%" stopColor="#059669" />
                </linearGradient>
              </defs>
              <rect x="4" y="5" width="16" height="16" rx="3" stroke="url(#gradBookings)" strokeWidth="2" fill="url(#gradBookings)" fillOpacity="0.1"/>
              <path d="M16 3V7" stroke="url(#gradBookings)" strokeWidth="2" strokeLinecap="round"/>
              <path d="M8 3V7" stroke="url(#gradBookings)" strokeWidth="2" strokeLinecap="round"/>
              <path d="M4 11H20" stroke="url(#gradBookings)" strokeWidth="2" strokeLinecap="round"/>
              <path d="M10 15L12 17L16 13" stroke="url(#gradBookings)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <span className="text-[13px] font-black text-[#002f34] uppercase tracking-wide">My Bookings</span>
        </div>

        {/* 4. My Ads */}
        <div onClick={() => handleNavigation('My Ad')} className="bg-white rounded-3xl p-5 flex flex-col items-center justify-center shadow-[0_8px_20px_rgba(0,0,0,0.04)] cursor-pointer active:scale-95 transition-all hover:shadow-lg border border-gray-50 aspect-square">
          <div className="w-20 h-20 mb-3 drop-shadow-[0_4px_10px_rgba(59,130,246,0.3)]">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
              <defs>
                <linearGradient id="gradMyAds" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#93C5FD" />
                  <stop offset="100%" stopColor="#2563EB" />
                </linearGradient>
              </defs>
              <path d="M5 10H19L21 15V19C21 19.5523 20.5523 20 20 20H19C18.4477 20 18 19.5523 18 19V18H6V19C6 19.5523 5.55228 20 5 20H4C3.44772 20 3 19.5523 3 19V15L5 10Z" stroke="url(#gradMyAds)" strokeWidth="2" fill="url(#gradMyAds)" fillOpacity="0.15" strokeLinejoin="round"/>
              <path d="M5 10L6.5 6H17.5L19 10" stroke="url(#gradMyAds)" strokeWidth="2" strokeLinejoin="round"/>
              <circle cx="7.5" cy="14.5" r="1.5" fill="url(#gradMyAds)"/>
              <circle cx="16.5" cy="14.5" r="1.5" fill="url(#gradMyAds)"/>
              <path d="M10 14H14" stroke="url(#gradMyAds)" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          <span className="text-[13px] font-black text-[#002f34] uppercase tracking-wide">My Ads</span>
        </div>

      </div>
    </div>
  );
}

export default MobileDashboard;